import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Wand2, Loader2, RefreshCw, Volume2 } from "lucide-react";
import { api } from "@/lib/api";

export function DessineMoi({ lang, speak, L, reward }) {
  const [input, setInput] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [image, setImage] = useState(null);
  const [caption, setCaption] = useState("");
  const [oops, setOops] = useState("");
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const rewardedRef = useRef(false);

  useEffect(() => () => { try { mrRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {} }, []);

  const generate = async (text) => {
    const wish = (text ?? input).trim();
    if (!wish || drawing) return;
    setDrawing(true);
    setOops("");
    setImage(null);
    setCaption("");
    try {
      const { data } = await api.post("/kids/draw", { text: wish, lang });
      if (data.safe && data.image) {
        setImage("data:" + (data.mime || "image/png") + ";base64," + data.image);
        setCaption(data.caption || "");
        if (data.caption) speak && speak(data.caption);
        if (!rewardedRef.current) { rewardedRef.current = true; reward && reward(); }
      } else {
        setOops(data.caption || L.dessineOops);
        speak && speak(data.caption || L.dessineOops);
      }
    } catch {
      setOops(L.dessineFail);
    } finally {
      setDrawing(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "voix.webm");
          const { data } = await api.post(`/kids/transcribe?lang=${lang}`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const txt = (data.text || "").trim();
          if (txt) { setInput(txt); generate(txt); }
        } catch {
          /* silencieux pour les enfants */
        } finally {
          setTranscribing(false);
        }
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      setOops(L.dessineMicDenied);
    }
  };

  const stopRec = () => {
    if (mrRef.current && recording) { mrRef.current.stop(); setRecording(false); }
  };

  const reset = () => { setImage(null); setCaption(""); setOops(""); setInput(""); };

  const busy = transcribing || drawing;

  return (
    <div data-testid="kids-dessine">
      {!image ? (
        <>
          <p className="text-center text-foreground/60 mb-6">{L.dessineHint}</p>

          {/* Micro géant */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <motion.button
              type="button"
              data-testid="dessine-mic-btn"
              onClick={recording ? stopRec : startRec}
              disabled={busy && !recording}
              whileTap={{ scale: 0.92 }}
              animate={recording ? { scale: [1, 1.08, 1] } : {}}
              transition={{ repeat: recording ? Infinity : 0, duration: 1 }}
              className={`w-28 h-28 rounded-full flex items-center justify-center border-4 transition-colors disabled:opacity-50 ${
                recording ? "border-rose-400 bg-rose-400/20 text-rose-200" : "border-primary/40 bg-primary/15 text-primary hover:bg-primary/25"
              }`}
            >
              {transcribing ? <Loader2 size={40} className="animate-spin" /> : recording ? <Square size={40} className="fill-current" /> : <Mic size={44} />}
            </motion.button>
            <span className="text-foreground/70 font-display text-lg">
              {transcribing ? L.dessineHearing : recording ? L.dessineListening : L.dessineMic}
            </span>
          </div>

          {/* Idées toutes prêtes */}
          <p className="text-center text-primary/70 text-sm mb-3">{L.dessineIdeasLabel}</p>
          <div className="flex flex-wrap justify-center gap-2 mb-6 max-w-xl mx-auto">
            {L.dessineIdeas.map((idea, i) => (
              <button
                key={i}
                data-testid={`dessine-idea-${i}`}
                onClick={() => { setInput(idea); generate(idea); }}
                disabled={busy}
                className="rounded-full px-4 py-2 text-sm border border-white/15 bg-white/5 text-foreground/80 hover:border-primary/50 hover:bg-primary/10 transition-colors disabled:opacity-40"
              >
                {idea}
              </button>
            ))}
          </div>

          {/* Saisie libre + bouton dessiner */}
          <div className="flex gap-2 max-w-md mx-auto">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && generate()}
              placeholder={L.dessinePlaceholder}
              data-testid="dessine-input"
              disabled={busy}
              className="flex-1 rounded-2xl bg-white/5 border-2 border-white/15 focus:border-primary/50 outline-none px-4 py-3 text-foreground/90"
            />
            <button
              onClick={() => generate()}
              disabled={busy || !input.trim()}
              data-testid="dessine-draw-btn"
              className="flex items-center gap-2 px-5 rounded-2xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40"
            >
              {drawing ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
              <span className="hidden sm:inline">{L.dessineDraw}</span>
            </button>
          </div>

          {drawing && (
            <p className="text-center text-primary/80 mt-6 font-display text-lg animate-pulse" data-testid="dessine-drawing">{L.dessineThinking}</p>
          )}
          {oops && !drawing && (
            <p className="text-center text-amber-300/90 mt-6 font-display text-lg" data-testid="dessine-oops">🌈 {oops}</p>
          )}
        </>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center" data-testid="dessine-result"
          >
            <div className="relative rounded-3xl overflow-hidden border-2 border-primary/30 shadow-2xl max-w-md w-full">
              <div className="dessine-sparkle" aria-hidden />
              <img src={image} alt={caption} className="w-full h-auto block" data-testid="dessine-image" />
            </div>
            <div className="flex items-center gap-3 mt-5 mb-1">
              <h3 className="font-display text-2xl text-gradient-orange italic text-center">{caption}</h3>
              {caption && (
                <button onClick={() => speak && speak(caption)} data-testid="dessine-listen-btn"
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-primary/15 text-primary hover:bg-primary/25 transition-colors">
                  <Volume2 size={16} />
                </button>
              )}
            </div>
            <button
              onClick={reset}
              data-testid="dessine-again-btn"
              className="mt-6 flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
            >
              <RefreshCw size={16} /> {L.dessineAgain}
            </button>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
